import { t } from "../locale";
import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { config, storageKey } from "../config";
import { initializeAnalytics } from "../portal/analytics";
import { emptyLibrary, readLibrary, saveLibrary } from "../storage";

export type Viewer = {
  userId: string;
  email: string;
  nickname: string;
  avatar: string;
  enabled: boolean;
  revision: number;
  createdAt: string;
  lastLoginAt?: string;
};
export type LibraryItem = {
  seriesId: string;
  title: string;
  saved: boolean;
  progress: { episode: number; seconds: number; updatedAt: number } | null;
};
type Change = {
  seriesId: string;
  title: string;
  saved?: boolean;
  progress?: { episode: number; seconds: number } | null;
};
type Library = { revision: number; items: LibraryItem[] };
const tokenKey = storageKey("viewer-session");
const guestKey = storageKey("guest-library");
const guestBatchKey = storageKey("guest-library-batch");
let memoryGuestBatch = "";
const changed = "yestv-viewer-session";
const getToken = () => {
  try {
    return localStorage.getItem(tokenKey) || "";
  } catch {
    return "";
  }
};
let memoryToken = getToken();
const currentToken = () => memoryToken;
function setToken(value: string) {
  memoryToken = value;
  try {
    if (value) localStorage.setItem(tokenKey, value);
    else localStorage.removeItem(tokenKey);
  } catch {
    /* Session still works until this page closes. */
  }
  window.dispatchEvent(new Event(changed));
}
export class ViewerError extends Error {
  constructor(
    message: string,
    public status: number,
  ) {
    super(message);
  }
}
async function request<T>(
  path: string,
  method = "GET",
  body?: unknown,
  token = currentToken(),
): Promise<T> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 10000);
  let response: Response;
  try {
    response = await fetch(`${config.apiBaseUrl}/v1/pwa/${path}`, {
      method,
      cache: "no-store",
      signal: controller.signal,
      keepalive: path === "library" && method === "PUT",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
  } catch {
    throw new ViewerError(t("Could not connect to your account."), 0);
  } finally {
    clearTimeout(timeout);
  }
  const data = await response.json();
  if (!response.ok) {
    if (response.status === 401 && token && token === currentToken())
      setToken("");
    throw new ViewerError(
      data.error?.message || t("Could not save. Please try again."),
      response.status,
    );
  }
  return data;
}
function guestItems(): LibraryItem[] {
  try {
    const saved = JSON.parse(localStorage.getItem(guestKey) || "null");
    if (Array.isArray(saved))
      return saved
        .filter(
          (x) =>
            x &&
            /^[a-z0-9][a-z0-9_-]{0,79}$/.test(x.seriesId) &&
            typeof x.title === "string" &&
            typeof x.saved === "boolean" &&
            (x.progress === null ||
              (Number.isInteger(x.progress?.episode) &&
                Number.isFinite(x.progress?.seconds) &&
                Number.isFinite(x.progress?.updatedAt))),
        )
        .slice(0, 100);
  } catch {
    /* Fall back to the existing guest library. */
  }
  const old = readLibrary();
  return [...new Set([...old.saved, ...Object.keys(old.progress)])]
    .slice(0, 100)
    .map((seriesId) => ({
      seriesId,
      title: seriesId,
      saved: old.saved.includes(seriesId),
      progress: old.progress[seriesId] || null,
    }));
}
function guestBatch(): string {
  try {
    memoryGuestBatch = localStorage.getItem(guestBatchKey) || memoryGuestBatch;
  } catch {
    /* Use this page's batch. */
  }
  if (!memoryGuestBatch) {
    memoryGuestBatch = crypto.randomUUID();
    try {
      localStorage.setItem(guestBatchKey, memoryGuestBatch);
    } catch {
      /* Memory fallback. */
    }
  }
  return memoryGuestBatch;
}
function saveGuest(items: LibraryItem[]) {
  memoryGuestBatch = crypto.randomUUID();
  try {
    localStorage.setItem(guestBatchKey, memoryGuestBatch);
    localStorage.setItem(guestKey, JSON.stringify(items));
  } catch {
    /* Browsing remains available without storage. */
  }
}
type ViewerContextType = {
  user: Viewer | null;
  ready: boolean;
  error: string;
  guestId: string;
  items: LibraryItem[];
  authenticate: (
    mode: "login" | "register",
    body: Record<string, string>,
  ) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  updateProfile: (nickname: string, avatar: string) => Promise<void>;
  changePassword: (currentPassword: string, password: string) => Promise<void>;
  changeItem: (change: Change) => Promise<void>;
};
const Context = createContext<ViewerContextType | null>(null);
export const useViewer = () => {
  const value = useContext(Context);
  if (!value) throw new Error("ViewerProvider required");
  return value;
};
export function ViewerProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<Viewer | null>(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState("");
  const [guestId, setGuestId] = useState("");
  const [items, setItems] = useState<LibraryItem[]>(guestItems);
  const itemsRef = useRef(items);
  const generation = useRef(0);
  const revision = useRef(0);
  const queue = useRef<Promise<void>>(Promise.resolve());
  const apply = (next: LibraryItem[]) => {
    itemsRef.current = next;
    setItems(next);
  };
  const applyLibrary = (data: Library) => {
    if (data.revision >= revision.current) {
      revision.current = data.revision;
      apply(data.items);
    }
  };
  async function refresh() {
    const token = currentToken();
    const gen = generation.current;
    if (!token) {
      setUser(null);
      apply(guestItems());
      setReady(true);
      return;
    }
    try {
      const [profile, library] = await Promise.all([
        request<Viewer>("account/me", "GET", undefined, token),
        request<Library>("library", "GET", undefined, token),
      ]);
      if (token !== currentToken() || gen !== generation.current) return;
      setUser(profile);
      applyLibrary(library);
      setError("");
      const guest = guestItems();
      if (guest.length) {
        const importId = guestBatch();
        const identity = await initializeAnalytics();
        if (!identity)
          throw new Error(
            t("Your device library will sync when the connection returns."),
          );
        const merged = await request<Library>(
          "library/import",
          "POST",
          { identityToken: identity.identityToken, importId, items: guest },
          token,
        );
        if (token !== currentToken() || gen !== generation.current) return;
        applyLibrary(merged);
        if (guestBatch() === importId) {
          saveGuest([]);
          saveLibrary({ ...emptyLibrary(), unlocked: readLibrary().unlocked });
        }
      }
    } catch (e) {
      if (token === currentToken() && gen === generation.current)
        setError(
          e instanceof Error
            ? e.message
            : t("Could not connect to your account."),
        );
    } finally {
      if (gen === generation.current) setReady(true);
    }
  }
  useEffect(() => {
    if (!currentToken()) {
      try {
        if (localStorage.getItem(guestKey) === null)
          saveGuest(itemsRef.current);
      } catch {
        /* Storage is optional. */
      }
    }
    void initializeAnalytics().then((session) => {
      if (session) setGuestId(session.visitorId);
    });
    void refresh();
    const sessionChanged = () => {
      generation.current++;
      revision.current = 0;
      setUser(null);
      apply(guestItems());
      setReady(false);
      setError("");
      void refresh();
    };
    const storageChanged = (e: StorageEvent) => {
      if (e.key === tokenKey || e.key === null) {
        memoryToken = getToken();
        sessionChanged();
      } else if (e.key === guestKey && !currentToken()) apply(guestItems());
    };
    const focus = () => {
      void refresh();
    };
    addEventListener(changed, sessionChanged);
    addEventListener("storage", storageChanged);
    addEventListener("focus", focus);
    addEventListener("online", focus);
    return () => {
      removeEventListener(changed, sessionChanged);
      removeEventListener("storage", storageChanged);
      removeEventListener("focus", focus);
      removeEventListener("online", focus);
      generation.current++;
    };
  }, []);
  async function authenticate(
    mode: "login" | "register",
    body: Record<string, string>,
  ) {
    const result = await request<{
      token: string;
      user: Viewer;
      environment: string;
    }>(`account/${mode}`, "POST", body, "");
    if (result.environment !== config.environment)
      throw new Error(t("Account environment mismatch."));
    setToken(result.token);
    setUser(result.user);
    setReady(true);
    await refresh();
  }
  async function logout() {
    const token = currentToken();
    setToken("");
    if (token) await request("account/logout", "POST", {}, token);
  }
  async function updateProfile(nickname: string, avatar: string) {
    if (!user) return;
    const token = currentToken();
    const next = await request<Viewer>(
      "account/me",
      "PUT",
      { nickname, avatar, expectedRevision: user.revision },
      token,
    );
    if (token === currentToken()) setUser(next);
  }
  async function changePassword(currentPassword: string, password: string) {
    const token = currentToken();
    await request(
      "account/password",
      "PUT",
      { currentPassword, password },
      token,
    );
    if (token === currentToken()) setToken("");
  }
  function changeItem(change: Change): Promise<void> {
    const token = currentToken();
    if (!token) {
      const old = itemsRef.current.find(
        (x) => x.seriesId === change.seriesId,
      ) || { ...change, saved: false, progress: null };
      const item: LibraryItem = {
        ...old,
        ...change,
        progress:
          change.progress === undefined
            ? old.progress
            : change.progress
              ? { ...change.progress, updatedAt: Date.now() }
              : null,
      };
      const next = [
        item,
        ...itemsRef.current.filter((x) => x.seriesId !== change.seriesId),
      ]
        .filter((x) => x.saved || x.progress)
        .slice(0, 100);
      apply(next);
      saveGuest(next);
      return Promise.resolve();
    }
    const gen = generation.current;
    const action = queue.current
      .catch(() => {})
      .then(async () => {
        if (token !== currentToken() || gen !== generation.current) return;
        try {
          const next = await request<Library>("library", "PUT", change, token);
          if (token === currentToken() && gen === generation.current) {
            applyLibrary(next);
            setError("");
          }
        } catch (e) {
          if (token === currentToken())
            setError(
              t(
                "Your latest change could not be synced. Please retry when connected.",
              ),
            );
          throw e;
        }
      });
    queue.current = action;
    return action;
  }
  return (
    <Context.Provider
      value={{
        user,
        ready,
        error,
        guestId,
        items,
        authenticate,
        logout,
        refresh,
        updateProfile,
        changePassword,
        changeItem,
      }}
    >
      {children}
    </Context.Provider>
  );
}
